import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';
import QuestionTemplate from './QuestionTemplate';
import usePreventScroll from '../hooks/usePreventScroll';
import './QuestionBank.css';

const QuestionBank = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('ALL');

  // Prevent background scrolling when modal is open
  usePreventScroll(showQuestionForm);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/questions/bank`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const questionsArray = Array.isArray(data) ? data : [];
      setQuestions(questionsArray);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setMessage('Error loading questions');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQuestion = async (questionData) => {
    try {
      const isEdit = Boolean(editingQuestion && editingQuestion.id);
      const url = isEdit
        ? `${API_URL}/api/questions/${editingQuestion.id}`
        : `${API_URL}/api/questions`;
      const method = isEdit ? 'PUT' : 'POST';
      const token = localStorage.getItem('token');
      
      if (!token) {
        setMessage('Error: You must be logged in as an admin to perform this action');
        setTimeout(() => setMessage(''), 5000);
        return;
      }

      // For question bank, don't require a section
      const questionPayload = {
        ...questionData,
        questionSection: null // Questions in bank don't need a section
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(questionPayload),
      });
      
      if (response.ok) {
        await response.json();
        setMessage(isEdit ? 'Question updated successfully!' : 'Question added to bank successfully!');
        setShowQuestionForm(false);
        setEditingQuestion(null);
        fetchQuestions();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        setMessage(`Error ${isEdit ? 'updating' : 'creating'} question: ${response.status} - ${errorText}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Exception saving question:', error);
      setMessage(`Error ${Boolean(editingQuestion) ? 'updating' : 'creating'} question: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (window.confirm('⚠️ WARNING: Are you sure you want to PERMANENTLY delete this question from the bank? This will remove it from ALL sections and cannot be undone.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/questions/${questionId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        });

        if (response.ok) {
          setMessage('Question permanently deleted from bank!');
          fetchQuestions();
          setTimeout(() => setMessage(''), 3000);
        } else {
          const errorText = await response.text();
          setMessage(`Error deleting question: ${errorText}`);
          setTimeout(() => setMessage(''), 5000);
        }
      } catch (error) {
        setMessage(`Error deleting question: ${error.message}`);
        setTimeout(() => setMessage(''), 5000);
      }
    }
  };

  const handleLockQuestion = async (questionId, isLocked) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = isLocked 
        ? `${API_URL}/api/questions/${questionId}/unlock`
        : `${API_URL}/api/questions/${questionId}/lock`;
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        setMessage(isLocked ? 'Question unlocked successfully!' : 'Question locked successfully!');
        fetchQuestions();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorText = await response.text();
        setMessage(`Error ${isLocked ? 'unlocking' : 'locking'} question: ${errorText}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      setMessage(`Error ${isLocked ? 'unlocking' : 'locking'} question: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const filteredQuestions = questions.filter(question => {
    const matchesSearch = 
      question.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      question.categoryTags?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDifficulty = filterDifficulty === 'ALL' || question.difficultyLevel === filterDifficulty;
    
    return matchesSearch && matchesDifficulty;
  });

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'EASY': 'green',
      'MEDIUM': 'blue',
      'HARD': 'orange',
      'EXPERT': 'red'
    };
    return colors[difficulty] || 'gray';
  };

  const getDifficultyBadge = (difficulty) => {
    const color = getDifficultyColor(difficulty);
    return (
      <span className={`difficulty-badge ${color}`}>
        {difficulty}
      </span>
    );
  };

  if (loading) {
    return <div className="question-bank">Loading...</div>;
  }

  return (
    <div className="question-bank">
      <div className="bank-header">
        <h2>📚 Question Bank</h2>
        <p className="bank-subtitle">Central repository of all questions - Add once, use anywhere!</p>
        <button 
          className="create-btn"
          onClick={() => { setEditingQuestion(null); setShowQuestionForm(true); }}
        >
          ➕ Add New Question to Bank
        </button>
      </div>

      {message && (
        <div className="message">
          {message}
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search questions by title, description, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filterDifficulty === 'ALL' ? 'active' : ''}`}
            onClick={() => setFilterDifficulty('ALL')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filterDifficulty === 'EASY' ? 'active' : ''}`}
            onClick={() => setFilterDifficulty('EASY')}
          >
            Easy
          </button>
          <button
            className={`filter-btn ${filterDifficulty === 'MEDIUM' ? 'active' : ''}`}
            onClick={() => setFilterDifficulty('MEDIUM')}
          >
            Medium
          </button>
          <button
            className={`filter-btn ${filterDifficulty === 'HARD' ? 'active' : ''}`}
            onClick={() => setFilterDifficulty('HARD')}
          >
            Hard
          </button>
        </div>
      </div>

      {/* Question Form */}
      {showQuestionForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <QuestionTemplate 
              onSubmit={handleSaveQuestion}
              onCancel={() => { setShowQuestionForm(false); setEditingQuestion(null); }}
              question={editingQuestion}
              sectionId={null} // No section for bank questions
            />
          </div>
        </div>
      )}

      {/* Questions Table */}
      {filteredQuestions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <p>No questions found in the bank</p>
          <small>Click "Add New Question to Bank" to create your first question</small>
        </div>
      ) : (
        <div className="questions-table-container">
          <table className="questions-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Difficulty</th>
                <th>Submissions</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuestions.map(question => (
                <tr key={question.id} className={(question.isLocked === true) ? 'locked-row' : ''}>
                  <td className="id-cell">{question.id}</td>
                  <td className="title-cell">
                    <div className="title-content">
                      <span className="title-text" title={question.title || 'No Title'}>
                        {question.title || 'No Title'}
                      </span>
                    </div>
                  </td>
                  <td className="difficulty-cell">
                    {getDifficultyBadge(question.difficultyLevel || 'EASY')}
                  </td>
                  <td className="submissions-cell">
                    {question.submissionsCount || 0}
                  </td>
                  <td className="status-cell">
                    {(question.isLocked === true) && (
                      <span className="lock-badge" title="Locked">🔒</span>
                    )}
                    {!(question.isLocked === true) && (
                      <span className="unlocked-badge" title="Unlocked">✓</span>
                    )}
                  </td>
                  <td className="actions-cell">
                    <div className="table-actions">
                      <button 
                        className="action-btn edit-btn-small"
                        onClick={() => {
                          setEditingQuestion(question);
                          setShowQuestionForm(true);
                        }}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button 
                        className={`action-btn ${(question.isLocked === true) ? "unlock-btn-small" : "lock-btn-small"}`}
                        onClick={() => handleLockQuestion(question.id, question.isLocked === true)}
                        title={(question.isLocked === true) ? "Unlock question" : "Lock question"}
                      >
                        {(question.isLocked === true) ? '🔓' : '🔒'}
                      </button>
                      <button 
                        className="action-btn delete-btn-small"
                        onClick={() => handleDeleteQuestion(question.id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="bank-footer">
        <p>Total Questions in Bank: {questions.length} (includes all existing questions from database) | Showing: {filteredQuestions.length}</p>
      </div>
    </div>
  );
};

export default QuestionBank;

